import * as THREE from "three";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

/**
 * Images par seconde d'animation précalculées : le shader fond deux images voisines, la marche reste
 * fluide, et chaque image coûte une ligne de texture par sommet du modèle.
 */
const VAT_FPS = 15;
const VAT_MAX_FRAMES = 30;
/** Largeur des textures : bien sous la limite de tout GPU, la hauteur s'ajuste au nombre de sommets. */
const VAT_TEXTURE_WIDTH = 2048;
/** Les clips joués par le calque de décor (`playClip`) : le reste n'est jamais montré. */
export const VAT_CLIPS = ["walk", "idle"] as const;

export interface VatClip {
  /** Première image du clip dans les textures. */
  start: number;
  frames: number;
  duration: number;
}

export interface VatMesh {
  /** Rang du maillage parmi les maillages animés du modèle, dans l'ordre de parcours de `root`. */
  index: number;
  geometry: THREE.BufferGeometry;
  positions: THREE.DataTexture;
  normals: THREE.DataTexture;
  vertices: number;
}

/**
 * Les poses d'un modèle animé, précalculées sommet par sommet : chaque image de chaque clip,
 * positions et normales dans le repère du modèle. Un lot d'instances les relit dans le shader
 * (`vatMaterial`), chacune à son instant : toute une foule tient en un appel de dessin par maillage,
 * sans squelette à animer ni à envoyer au GPU à chaque image.
 */
export interface VatBake {
  meshes: VatMesh[];
  clips: Map<string, VatClip>;
}

/** Porté par un os sans être déformé par le squelette (une barbe, un sac à dos) : il suit l'os tel quel. */
function isBoneCarried(node: THREE.Object3D): boolean {
  for (let parent = node.parent; parent; parent = parent.parent) if ((parent as THREE.Bone).isBone) return true;
  return false;
}

/** Animé par les poses précalculées : déformé par le squelette, ou porté par un os. */
export function isVatMesh(node: THREE.Object3D): node is THREE.Mesh {
  return node instanceof THREE.SkinnedMesh || (node instanceof THREE.Mesh && !(node instanceof THREE.InstancedMesh) && isBoneCarried(node));
}

/**
 * Les maillages qu'animent les poses précalculées, dans l'ordre de parcours de `root` : le même pour
 * le modèle précalculé et pour chacune de ses copies, d'où `VatMesh.index`.
 */
export function vatMeshesOf(root: THREE.Object3D): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  root.traverse((node) => {
    if (isVatMesh(node)) meshes.push(node);
  });
  return meshes;
}

/** Un précalcul en cours, mené par tranches pour ne jamais figer l'affichage. */
export interface VatBaker {
  /** Avance jusqu'à l'instant `deadline` (`performance.now()`), au moins d'une image d'animation ; rend le résultat une fois fini. */
  step(deadline: number): VatBake | null;
}

/** `null` quand le modèle ne s'y prête pas : déformations par cibles, ou aucun clip connu. */
export function startVatBake(root: THREE.Object3D, animations: THREE.AnimationClip[]): VatBaker | null {
  const clips = VAT_CLIPS.map((name) => animations.find((clip) => clip.name === name)).filter((clip): clip is THREE.AnimationClip => !!clip);
  if (!clips.length) return null;
  const subject = cloneSkinned(root);
  subject.position.set(0, 0, 0);
  subject.rotation.set(0, 0, 0);
  subject.scale.set(1, 1, 1);
  const skinned = vatMeshesOf(subject);
  if (!skinned.some((mesh) => mesh instanceof THREE.SkinnedMesh) || skinned.some((mesh) => Object.keys(mesh.geometry.morphAttributes).length > 0)) return null;

  const layout = new Map<string, VatClip>();
  const steps: { clip: THREE.AnimationClip; frame: number; frames: number; start: number }[] = [];
  let totalFrames = 0;
  for (const clip of clips) {
    const frames = Math.max(2, Math.min(VAT_MAX_FRAMES, Math.round(clip.duration * VAT_FPS)));
    layout.set(clip.name, { start: totalFrames, frames, duration: clip.duration });
    for (let frame = 0; frame < frames; frame++) steps.push({ clip, frame, frames, start: totalFrames });
    totalFrames += frames;
  }

  const mixer = new THREE.AnimationMixer(subject);
  // Positions en demi-précision (le millimètre près sur une silhouette de deux mètres), normales sur un
  // octet par composante : la mémoire des poses tombe au tiers de leur version en flottants.
  const buffers = skinned.map((mesh) => {
    const skin = skinOf(mesh);
    const height = Math.ceil((skin.count * totalFrames) / VAT_TEXTURE_WIDTH);
    const texels = VAT_TEXTURE_WIDTH * height * 4;
    // Des tampons explicites : la texture n'accepte qu'un tableau adossé à un `ArrayBuffer`.
    return {
      skin,
      height,
      positions: new Uint16Array(new ArrayBuffer(texels * 2)),
      normals: new Uint8Array(new ArrayBuffer(texels)),
      posed: new Float32Array(skin.count * 4),
      shaded: new Float32Array(skin.count * 4),
    };
  });

  const finish = (): VatBake => {
    const texture = (data: (typeof buffers)[number]["positions"] | (typeof buffers)[number]["normals"], height: number, type: THREE.TextureDataType) => {
      const map = new THREE.DataTexture(data, VAT_TEXTURE_WIDTH, height, THREE.RGBAFormat, type);
      map.minFilter = THREE.NearestFilter;
      map.magFilter = THREE.NearestFilter;
      map.generateMipmaps = false;
      map.needsUpdate = true;
      // Une fois sur le GPU, la copie gardée en mémoire ne sert plus à rien : elle doublait la note.
      map.onUpdate = () => {
        (map.image as { data: ArrayBufferView | null }).data = null;
      };
      return map;
    };
    return {
      clips: layout,
      meshes: skinned.map((mesh, index) => {
        const buffer = buffers[index]!;
        const geometry = new THREE.BufferGeometry();
        for (const [name, attribute] of Object.entries(mesh.geometry.attributes)) {
          if (name !== "skinIndex" && name !== "skinWeight") geometry.setAttribute(name, attribute);
        }
        if (mesh.geometry.index) geometry.setIndex(mesh.geometry.index);
        for (const group of mesh.geometry.groups) geometry.addGroup(group.start, group.count, group.materialIndex);
        geometry.setAttribute("vatVertex", new THREE.BufferAttribute(Float32Array.from({ length: buffer.skin.count }, (_, i) => i), 1));
        return { index, geometry, positions: texture(buffer.positions, buffer.height, THREE.HalfFloatType), normals: texture(buffer.normals, buffer.height, THREE.UnsignedByteType), vertices: buffer.skin.count };
      }),
    };
  };

  let next = 0;
  let playing: THREE.AnimationAction | null = null;
  return {
    step(deadline) {
      do {
        const { clip, frame, frames, start } = steps[next++]!;
        if (playing?.getClip() !== clip) {
          mixer.stopAllAction();
          playing = mixer.clipAction(clip);
          playing.play();
        }
        playing.time = (frame / frames) * clip.duration;
        mixer.update(0);
        subject.updateMatrixWorld(true);
        skinned.forEach((mesh, meshIndex) => {
          const buffer = buffers[meshIndex]!;
          if (mesh instanceof THREE.SkinnedMesh) skinPose(mesh, buffer.posed, 0, buffer.skin);
          else carriedPose(mesh, buffer.posed, buffer.skin);
          poseNormals(buffer.posed, buffer.shaded, 0, buffer.skin.count, mesh.geometry.index);
          packPose(buffer.posed, buffer.shaded, buffer.positions, buffer.normals, (start + frame) * buffer.skin.count * 4);
        });
      } while (next < steps.length && performance.now() < deadline);
      if (next < steps.length) return null;
      mixer.stopAllAction();
      return finish();
    },
  };
}

/** La pose d'un maillage porté par un os : ses sommets tels que l'os les place, écrits en `xyzw`. */
export function carriedPose(mesh: THREE.Mesh, target: Float32Array, skin: SkinData): void {
  const m = mesh.matrixWorld.elements;
  const { count, positions } = skin;
  for (let i = 0; i < count; i++) {
    const x = positions[i * 3]!;
    const y = positions[i * 3 + 1]!;
    const z = positions[i * 3 + 2]!;
    target[i * 4] = m[0]! * x + m[4]! * y + m[8]! * z + m[12]!;
    target[i * 4 + 1] = m[1]! * x + m[5]! * y + m[9]! * z + m[13]!;
    target[i * 4 + 2] = m[2]! * x + m[6]! * y + m[10]! * z + m[14]!;
    target[i * 4 + 3] = 1;
  }
}

/**
 * Une image de pose rangée dans les textures, à partir de `offset` : positions en demi-précision,
 * normales de [-1, 1] ramenées sur un octet (le shader les remet en place).
 */
export function packPose(posed: Float32Array, shaded: Float32Array, positions: Uint16Array, normals: Uint8Array, offset: number): void {
  for (let i = 0; i < posed.length; i++) {
    positions[offset + i] = THREE.DataUtils.toHalfFloat(posed[i]!);
    normals[offset + i] = Math.round((Math.max(-1, Math.min(1, shaded[i]!)) * 0.5 + 0.5) * 255);
  }
}

/** Positions, os et poids des sommets, lus une fois pour toutes les images d'animation. */
interface SkinData {
  count: number;
  positions: Float32Array;
  bones: Uint16Array;
  weights: Float32Array;
}

/** Un maillage porté par un os n'a ni os ni poids par sommet : seules ses positions sont lues. */
export function skinOf(mesh: THREE.Mesh): SkinData {
  const geometry = mesh.geometry;
  const position = geometry.getAttribute("position") as THREE.BufferAttribute;
  const skinIndex = geometry.getAttribute("skinIndex") as THREE.BufferAttribute | undefined;
  const skinWeight = geometry.getAttribute("skinWeight") as THREE.BufferAttribute | undefined;
  const count = position.count;
  const positions = new Float32Array(count * 3);
  const deformed = !!(skinIndex && skinWeight);
  const bones = new Uint16Array(deformed ? count * 4 : 0);
  const weights = new Float32Array(deformed ? count * 4 : 0);
  for (let i = 0; i < count; i++) {
    positions[i * 3] = position.getX(i);
    positions[i * 3 + 1] = position.getY(i);
    positions[i * 3 + 2] = position.getZ(i);
    if (!skinIndex || !skinWeight) continue;
    for (let k = 0; k < 4; k++) {
      bones[i * 4 + k] = skinIndex.getComponent(i, k);
      weights[i * 4 + k] = skinWeight.getComponent(i, k);
    }
  }
  return { count, positions, bones, weights };
}

/**
 * La pose courante du maillage, sommet par sommet, écrite en `xyzw` à partir de `offset` : le calcul de
 * `SkinnedMesh.applyBoneTransform`, matrices de chaque os combinées une fois pour toutes. Sommet par
 * sommet, `applyBoneTransform` coûtait près de 300 ms par modèle, et figeait la carte à l'ouverture.
 */
export function skinPose(mesh: THREE.SkinnedMesh, target: Float32Array, offset: number, skin: SkinData = skinOf(mesh)): void {
  const { bones, boneInverses } = mesh.skeleton;
  const bone = new THREE.Matrix4();
  const boneMatrices = new Float32Array(bones.length * 16);
  bones.forEach((node, index) => {
    bone.multiplyMatrices(node.matrixWorld, boneInverses[index]!).multiply(mesh.bindMatrix);
    boneMatrices.set(bone.elements, index * 16);
  });
  const after = new THREE.Matrix4().multiplyMatrices(mesh.matrixWorld, mesh.bindMatrixInverse).elements;
  const { count, positions, bones: vertexBones, weights } = skin;
  for (let i = 0; i < count; i++) {
    const px = positions[i * 3]!;
    const py = positions[i * 3 + 1]!;
    const pz = positions[i * 3 + 2]!;
    let x = 0;
    let y = 0;
    let z = 0;
    for (let k = 0; k < 4; k++) {
      const weight = weights[i * 4 + k]!;
      if (weight === 0) continue;
      const m = vertexBones[i * 4 + k]! * 16;
      x += weight * (boneMatrices[m]! * px + boneMatrices[m + 4]! * py + boneMatrices[m + 8]! * pz + boneMatrices[m + 12]!);
      y += weight * (boneMatrices[m + 1]! * px + boneMatrices[m + 5]! * py + boneMatrices[m + 9]! * pz + boneMatrices[m + 13]!);
      z += weight * (boneMatrices[m + 2]! * px + boneMatrices[m + 6]! * py + boneMatrices[m + 10]! * pz + boneMatrices[m + 14]!);
    }
    const at = offset + i * 4;
    target[at] = after[0]! * x + after[4]! * y + after[8]! * z + after[12]!;
    target[at + 1] = after[1]! * x + after[5]! * y + after[9]! * z + after[13]!;
    target[at + 2] = after[2]! * x + after[6]! * y + after[10]! * z + after[14]!;
    target[at + 3] = 1;
  }
}

/** Les normales de la pose écrite par `skinPose`, comme `BufferGeometry.computeVertexNormals` : chaque face y pèse selon son aire. */
export function poseNormals(positions: Float32Array, target: Float32Array, offset: number, count: number, index: THREE.BufferAttribute | null): void {
  target.fill(0, offset, offset + count * 4);
  const corners = index?.array;
  const triangles = corners ? index!.count / 3 : count / 3;
  for (let face = 0; face < triangles; face++) {
    const a = corners ? corners[face * 3]! : face * 3;
    const b = corners ? corners[face * 3 + 1]! : face * 3 + 1;
    const c = corners ? corners[face * 3 + 2]! : face * 3 + 2;
    const pa = offset + a * 4;
    const pb = offset + b * 4;
    const pc = offset + c * 4;
    const e1x = positions[pc]! - positions[pb]!;
    const e1y = positions[pc + 1]! - positions[pb + 1]!;
    const e1z = positions[pc + 2]! - positions[pb + 2]!;
    const e2x = positions[pa]! - positions[pb]!;
    const e2y = positions[pa + 1]! - positions[pb + 1]!;
    const e2z = positions[pa + 2]! - positions[pb + 2]!;
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;
    target[pa] += nx;
    target[pa + 1] += ny;
    target[pa + 2] += nz;
    target[pb] += nx;
    target[pb + 1] += ny;
    target[pb + 2] += nz;
    target[pc] += nx;
    target[pc + 1] += ny;
    target[pc + 2] += nz;
  }
  for (let i = 0; i < count; i++) {
    const at = offset + i * 4;
    const length = Math.hypot(target[at]!, target[at + 1]!, target[at + 2]!) || 1;
    target[at] /= length;
    target[at + 1] /= length;
    target[at + 2] /= length;
  }
}

const VAT_HEAD = /* glsl */ `
  uniform sampler2D uVatPositions;
  uniform sampler2D uVatNormals;
  uniform vec2 uVatSize;
  uniform float uVatVertices;
  attribute float vatVertex;
  attribute vec3 vatClip;
  vec2 vatUv(float frame) {
    float index = frame * uVatVertices + vatVertex;
    return (vec2(mod(index, uVatSize.x), floor(index / uVatSize.x)) + 0.5) / uVatSize;
  }
  vec4 vatSample(sampler2D map) {
    float frame = vatClip.z * vatClip.y;
    float first = min(floor(frame), vatClip.y - 1.0);
    // \`mod(n, n)\` rend parfois \`n\` sur GPU : la dernière image se fondait alors dans une ligne hors du clip.
    float next = first + 1.0 < vatClip.y ? first + 1.0 : 0.0;
    return mix(texture2D(map, vatUv(vatClip.x + first)), texture2D(map, vatUv(vatClip.x + next)), fract(frame));
  }
`;

const materials = new WeakMap<THREE.Material, Map<VatMesh, THREE.Material>>();

/**
 * Le matériau du maillage, qui lit sa pose dans les textures au lieu d'un squelette. `vatClip` est
 * propre à chaque instance : première image du clip, nombre d'images, avancement de 0 à 1.
 */
export function vatMaterial(base: THREE.Material, mesh: VatMesh): THREE.Material {
  let byMesh = materials.get(base);
  if (!byMesh) materials.set(base, (byMesh = new Map()));
  const cached = byMesh.get(mesh);
  if (cached) return cached;
  const material = base.clone();
  const previous = base.onBeforeCompile;
  const uniforms = {
    uVatPositions: { value: mesh.positions },
    uVatNormals: { value: mesh.normals },
    uVatSize: { value: new THREE.Vector2(mesh.positions.image.width, mesh.positions.image.height) },
    uVatVertices: { value: mesh.vertices },
  };
  material.onBeforeCompile = (shader, renderer) => {
    previous.call(base, shader, renderer);
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>\n${VAT_HEAD}`)
      .replace("#include <beginnormal_vertex>", "vec3 objectNormal = normalize(vatSample(uVatNormals).xyz * 2.0 - 1.0);\n#ifdef USE_TANGENT\nvec3 objectTangent = vec3(tangent.xyz);\n#endif")
      .replace("#include <begin_vertex>", "vec3 transformed = vatSample(uVatPositions).xyz;");
  };
  const baseKey = base.customProgramCacheKey();
  material.customProgramCacheKey = () => `${baseKey}|vat`;
  byMesh.set(mesh, material);
  return material;
}
